import { useMemo, useState } from "react";
import type { DependencyList, Ref } from "react";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useEntitiesById } from "@hooks/useEntities";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import {
    FINANCIAL_TYPES_AVAILABLE_FOR_RECONCILIATION,
    getBookkeepingStartDate,
} from "@common/helpers/reconciliation";
import { FinancialAccountType } from "@common/types/financialAccount";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { useBankReconciliations } from "@api/reconciliation.api";
import { useUnreconciledKickBalancesForEntities } from "@api/openingBalance.api";
import { groupBy } from "@common/helpers/collections";
import {
    BankReconciliationRow,
    bankReconciliationViewConfig,
} from "./views/bankReconciliationViewConfig";
import { defaultBankReconciliationView } from "./views/defaultReconciliationViews";
import {
    BankReconciliationColumn,
    BankReconciliationView,
} from "./views/bankReconciliationView";
import {
    DEFAULT_BANK_RECONCILIATION_SORT,
    sortBankReconciliationRows,
} from "./views/bankReconciliationSort";

interface Props {
    entityIds: number[];
    expanded?: ExpandedState;
    onExpandedChange?: OnChangeFn<ExpandedState>;
    /** When provided, only accounts whose financialType is in this list are shown. */
    financialAccountTypeFilter?: FinancialAccountType[];
    /** When provided (non-empty), only accounts whose id is in this list are shown. Takes precedence over `financialAccountTypeFilter`. */
    financialAccountIdFilter?: number[];
    bankReconciliationTableClassName?: string;
    /** Overrides the table view. Defaults to the multi-entity view. */
    view?: BankReconciliationView;
    /**
     * Forwarded to `ViewTable` to force a top-offset re-measure on layout
     * shifts the `ResizeObserver` can't catch (e.g. a fullscreen toggle that
     * hides the app header without resizing the table).
     */
    recomputeTopOffsetOn?: DependencyList;
}

export const BankReconciliation = ({
    entityIds,
    expanded: controlledExpanded,
    onExpandedChange,
    financialAccountTypeFilter,
    financialAccountIdFilter,
    bankReconciliationTableClassName,
    view = defaultBankReconciliationView,
    recomputeTopOffsetOn,
    ref,
}: Props & { ref?: Ref<ViewTableHandle> }) => {
    const [uncontrolledExpanded, setUncontrolledExpanded] =
        useState<ExpandedState>({});
    const expanded = controlledExpanded ?? uncontrolledExpanded;
    const setExpanded = onExpandedChange ?? setUncontrolledExpanded;
    const [sort, setSort] = useState(DEFAULT_BANK_RECONCILIATION_SORT);
    const {
        personalAccountReconciliation: isPersonalReconciliationEnabled,
        continuousReconciliation: isContinuousReconciliationEnabled,
    } = useTypedFlags();
    const visibleView: BankReconciliationView = useMemo(
        () =>
            isContinuousReconciliationEnabled
                ? view
                : {
                      ...view,
                      columns: view.columns.filter(
                          (column) =>
                              column.id !==
                              BankReconciliationColumn.CONTINUOUS_RECONCILIATION,
                      ),
                  },
        [view, isContinuousReconciliationEnabled],
    );
    const entitiesById = useEntitiesById({
        onlyBusiness: !isPersonalReconciliationEnabled,
        excludeMockEntities: true,
    });
    const financialAccounts = useFinancialAccounts({
        entityIds,
        onlyBusiness: !isPersonalReconciliationEnabled,
        excludeMockEntities: true,
    });
    const { data: reconciliations } = useBankReconciliations(entityIds);
    const { data: unreconciledKickBalances = [] } =
        useUnreconciledKickBalancesForEntities(entityIds);

    const data: BankReconciliationRow[] = useMemo(() => {
        const reconciliationsByFinancialAccountId = groupBy(
            reconciliations,
            "financialAccountId",
        );
        const unreconciledKickBalanceMap = Object.fromEntries(
            unreconciledKickBalances.map((balance) => [
                balance.financialAccountId,
                balance.kickOpeningBalance,
            ]),
        );
        const effectiveFilter = financialAccountTypeFilter?.length
            ? financialAccountTypeFilter
            : FINANCIAL_TYPES_AVAILABLE_FOR_RECONCILIATION;

        return financialAccounts
            .filter((financialAccount) =>
                effectiveFilter.includes(financialAccount.financialType),
            )
            .filter(
                (financialAccount) =>
                    !financialAccountIdFilter?.length ||
                    financialAccountIdFilter.includes(financialAccount.id),
            )
            .map((account) => {
                const accountReconciliations =
                    reconciliationsByFinancialAccountId[account.id];
                const firstReconciliation = accountReconciliations?.[0];
                const lastReconciliation =
                    accountReconciliations?.[accountReconciliations.length - 1];
                const accountEntity = account.entityId
                    ? entitiesById.get(account.entityId)
                    : undefined;
                return {
                    id: account.id,
                    reconciliationNumber: accountReconciliations?.length ?? 0,
                    financialAccount: account,
                    entity: accountEntity,
                    mostRecentReconciliation: lastReconciliation,
                    openingDate:
                        firstReconciliation?.openingDate ??
                        (accountEntity
                            ? getBookkeepingStartDate(account, accountEntity)
                            : (account.bookkeepingStartDate ?? "")),
                    closingDate: lastReconciliation?.closingDate,
                    openingBalance:
                        firstReconciliation?.openingBalance ??
                        unreconciledKickBalanceMap[account.id] ??
                        undefined,
                    closingBalance: lastReconciliation?.closingBalance,
                    reconciliations: accountReconciliations?.map(
                        (reconciliation, index) => ({
                            ...reconciliation,
                            financialAccount: account,
                            entity: accountEntity,
                            reconciliationNumber: index,
                            bankReconciliation: reconciliation,
                            isLastReconciliation:
                                reconciliation.id === lastReconciliation?.id,
                        }),
                    ),
                };
            });
    }, [
        financialAccounts,
        reconciliations,
        entitiesById,
        unreconciledKickBalances,
        financialAccountTypeFilter,
        financialAccountIdFilter,
    ]);

    const sortedData = useMemo(
        () => sortBankReconciliationRows(data, sort),
        [data, sort],
    );

    return (
        <ViewTable
            ref={ref}
            data={sortedData}
            className={bankReconciliationTableClassName}
            view={visibleView}
            config={bankReconciliationViewConfig}
            sort={sort}
            onSortChange={setSort}
            enableColumnResizing={false}
            expanded={expanded}
            onExpandedChange={setExpanded}
            nestingIndentPx={24}
            fixedTopOffsetPx
            recomputeTopOffsetOn={recomputeTopOffsetOn}
            emptyView={() => (
                <EmptyState header="No bank accounts found for the selected entities" />
            )}
        />
    );
};
